import { useEffect, useState, type FormEvent } from 'react'
import type { CategoryGroup } from '@/features/budget/types'
import type { Transaction } from './types'
import type { SplitInput } from './repository'
import { useUpdateSplits } from './hooks'
import { CategoryDropdown } from './CategoryDropdown'

interface Props {
  open: boolean
  onClose: () => void
  transaction: Transaction
  groups: CategoryGroup[]
}

interface DraftRow {
  uid: string
  categoryId: string | null
  amount: string // string to keep the user's typing intact while validating
  memo: string
}

/**
 * Split a single transaction across N categories.
 *
 * Rules
 *  - At least 2 rows required (otherwise just use inline category edit)
 *  - Every row must have a category and a positive (absolute) amount
 *  - Sum of split amounts must equal the parent amount, with 1¢
 *    rounding tolerance (IEEE-754 wiggle)
 *  - Sign is preserved: outflows stay negative, inflows positive
 *  - "Remaining" footer shows how much is still unallocated so the
 *    user can fix splits without doing the math
 *  - "Clear splits" reverts to a single-category transaction
 */
export function SplitDialog({ open, onClose, transaction, groups }: Props) {
  const mutation = useUpdateSplits()
  const [rows, setRows] = useState<DraftRow[]>([])
  const [error, setError] = useState<string | null>(null)

  const isOutflow = transaction.amount < 0
  const sign = isOutflow ? -1 : 1
  const parentAbs = Math.abs(transaction.amount)

  useEffect(() => {
    if (!open) return
    setError(null)
    if (transaction.splits.length > 0) {
      setRows(
        transaction.splits.map((s) => ({
          uid: crypto.randomUUID(),
          categoryId: s.categoryId,
          amount: Math.abs(s.amount).toFixed(2),
          memo: s.memo ?? '',
        })),
      )
    } else {
      // Seed two empty rows — the minimum useful split. Pre-populating
      // amounts to half the parent is a nicer default than blank.
      const half = (parentAbs / 2).toFixed(2)
      setRows([
        { uid: crypto.randomUUID(), categoryId: null, amount: half, memo: '' },
        { uid: crypto.randomUUID(), categoryId: null, amount: half, memo: '' },
      ])
    }
  }, [open, transaction, parentAbs])

  if (!open) return null

  const totals = rows.reduce(
    (acc, r) => {
      const n = Number(r.amount.replace(/[$,]/g, ''))
      if (Number.isFinite(n) && n > 0) acc += n
      return acc
    },
    0,
  )
  const remaining = parentAbs - totals
  const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

  function updateRow(uid: string, patch: Partial<DraftRow>) {
    setRows((prev) => prev.map((r) => (r.uid === uid ? { ...r, ...patch } : r)))
  }
  function addRow() {
    setRows((prev) => [
      ...prev,
      { uid: crypto.randomUUID(), categoryId: null, amount: '0.00', memo: '' },
    ])
  }
  function removeRow(uid: string) {
    setRows((prev) => prev.filter((r) => r.uid !== uid))
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (rows.length < 2) {
      setError('A split needs at least 2 categories. Cancel to use single-category edit instead.')
      return
    }
    const splits: SplitInput[] = []
    for (const r of rows) {
      if (!r.categoryId) {
        setError('Every split row needs a category')
        return
      }
      const n = Number(r.amount.replace(/[$,]/g, ''))
      if (!Number.isFinite(n) || n <= 0) {
        setError('Every split row needs a positive amount')
        return
      }
      splits.push({
        categoryId: r.categoryId,
        amount: sign * n,
        memo: r.memo.trim() ? r.memo.trim() : null,
      })
    }
    mutation.mutate(
      { transaction, splits },
      {
        onSuccess: () => onClose(),
        onError: (err) => setError(err instanceof Error ? err.message : 'Save failed'),
      },
    )
  }

  function clear() {
    mutation.mutate(
      { transaction, splits: [] },
      {
        onSuccess: () => onClose(),
        onError: (err) => setError(err instanceof Error ? err.message : 'Clear failed'),
      },
    )
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="split-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose()
      }}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-xl space-y-3 rounded-2xl border border-zinc-200 bg-white p-6 shadow-xl dark:border-zinc-800 dark:bg-zinc-900"
      >
        <h2 id="split-title" className="text-lg font-semibold">
          Split transaction
        </h2>
        <p className="text-xs text-zinc-500">
          {transaction.description ?? transaction.memo ?? 'Transaction'} —{' '}
          {usd.format(transaction.amount)}
        </p>

        <div className="space-y-2">
          {rows.map((row, idx) => (
            <div
              key={row.uid}
              className="grid grid-cols-[1fr_120px_28px] items-center gap-2 rounded-md border border-zinc-200 p-2 dark:border-zinc-800"
            >
              <div>
                <CategoryDropdown
                  groups={groups}
                  value={row.categoryId}
                  onChange={(id) => updateRow(row.uid, { categoryId: id })}
                  highlightEmpty
                />
                <input
                  type="text"
                  value={row.memo}
                  onChange={(e) => updateRow(row.uid, { memo: e.target.value })}
                  placeholder="Memo (optional)"
                  aria-label={`Split ${idx + 1} memo`}
                  className="mt-1 w-full rounded border border-zinc-300 bg-white px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-950"
                />
              </div>
              <input
                type="text"
                inputMode="decimal"
                value={row.amount}
                onChange={(e) => updateRow(row.uid, { amount: e.target.value })}
                aria-label={`Split ${idx + 1} amount`}
                className="w-full rounded border border-zinc-300 bg-white px-2 py-1 text-right text-sm tabular-nums dark:border-zinc-700 dark:bg-zinc-950"
              />
              <button
                type="button"
                onClick={() => removeRow(row.uid)}
                aria-label={`Remove split ${idx + 1}`}
                disabled={rows.length <= 2}
                className="text-zinc-400 hover:text-overspent disabled:opacity-30"
              >
                ×
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addRow}
          className="text-sm text-blue-600 hover:underline dark:text-blue-400"
        >
          + Add split
        </button>

        <div
          className={`flex items-center justify-between rounded-md px-3 py-2 text-sm ${
            Math.abs(remaining) <= 0.01
              ? 'bg-funded-subtle text-funded'
              : 'bg-zinc-100 dark:bg-zinc-800'
          }`}
        >
          <span>Allocated</span>
          <span className="tabular-nums">
            {usd.format(totals)} / {usd.format(parentAbs)}
          </span>
        </div>
        {Math.abs(remaining) > 0.01 && (
          <p className="text-xs text-zinc-500">
            {remaining > 0
              ? `${usd.format(remaining)} unallocated`
              : `Over-allocated by ${usd.format(Math.abs(remaining))}`}
          </p>
        )}

        {error && (
          <p role="alert" className="text-sm text-overspent">
            {error}
          </p>
        )}

        <div className="flex justify-between gap-2 pt-2">
          {transaction.splits.length > 0 ? (
            <button
              type="button"
              onClick={clear}
              disabled={mutation.isPending}
              className="text-sm text-overspent hover:underline disabled:opacity-50"
            >
              Clear splits
            </button>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-zinc-300 px-4 py-2 text-sm hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || Math.abs(remaining) > 0.01}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {mutation.isPending ? 'Saving…' : 'Save splits'}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
